import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowRight,
  Globe2,
  ShoppingBag,
  Stamp,
  TrendingUp,
  Menu,
  X,
  Globe,
  Eye,
  Check,
  Hammer,
  CreditCard,
  Smartphone,
  Zap,
  Building2,
  Sparkle,
  UtensilsCrossed,
  Package,
  Truck,
  Coffee,
  Beer,
  Sparkles,
  Megaphone,
  BadgePercent,
  Bell,
  QrCode,
  Users,
  Heart,
  LayoutGrid,
} from "lucide-react";
import { publicClient } from "../config/axiosConfig";

/**
 * Palette (token system)
 * --ink:      #14130F  near-black, warm — backgrounds, primary text on cream
 * --cream:    #F6F3EC  off-white — page background
 * --cream-2:  #FBF9F4  card surface, slightly lighter than page bg
 * --green:    #1E4B3A  deep forest — brand chrome, secondary actions, Loyalty section
 * --green-2:  #2F6B52  hover state for green
 * --red:      #B23A2E  brick/appetite red — CTAs, stamps, single accent
 * --red-2:    #8F2C22  hover state for red
 * --muted:    #6B6A61  secondary text on cream
 * --muted-ink:#A9A79C  secondary text on ink
 *
 * Type: Fraunces (display serif, editorial/menu character) + Inter (UI/body)
 * + a mono face for small labels ("THE CORE OF ODIJN", stat callouts).
 * Move the @import below into your global stylesheet / index.html for
 * production — it's inlined here just so this file is self-contained.
 *
 * Page flow: Hero (digital menu, the core product) -> Categories (who it's
 * for) -> Loyalty (the retention layer) -> Why Odijn -> Features -> Footer.
 */

const FONT_IMPORT_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;9..144,500;9..144,600&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');
  .font-display { font-family: 'Fraunces', serif; font-optical-sizing: auto; }
  .font-body { font-family: 'Inter', sans-serif; }
  .font-mono-label { font-family: 'JetBrains Mono', monospace; letter-spacing: 0.08em; }
`;

export default function IntroOdijn() {
  const [menuOpen, setMenuOpen] = useState(false);

  const navItems = [
    { label: "Menu", href: "#hero", icon: QrCode },
    { label: "For You", href: "#categories", icon: Users },
    { label: "Loyalty", href: "#loyalty", icon: Stamp },
    { label: "Why Us", href: "#about", icon: Heart },
    { label: "Features", href: "#features", icon: LayoutGrid },
    { label: "Marketing", href: "#marketing", icon: Megaphone },
    { label: "Pricing", href: "#pricing", icon: BadgePercent },
  ];

  // Nav polish: condense on scroll, and track which section is in view
  // so the nav can highlight it with a sliding underline.
  const [navScrolled, setNavScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("hero");

  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const ids = navItems.map((item) => item.href.replace("#", ""));
    const sections = ids
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 },
    );

    sections.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Free-to-use photos from Pexels (pexels.com/license — no attribution
  // required). Hotlinked here for convenience; for production, download
  // and serve these from your own CDN/asset pipeline instead.
  const PHOTOS = {
    // Vietnamese
    springRolls:
      "https://images.pexels.com/photos/6646031/pexels-photo-6646031.jpeg?auto=compress&cs=tinysrgb&w=200",
    pho: "https://images.pexels.com/photos/699953/pexels-photo-699953.jpeg?auto=compress&cs=tinysrgb&w=200",
    // Western
    burger:
      "https://images.pexels.com/photos/1556698/pexels-photo-1556698.jpeg?auto=compress&cs=tinysrgb&w=200",
    fries:
      "https://images.pexels.com/photos/15656541/pexels-photo-15656541.jpeg?auto=compress&cs=tinysrgb&w=200",
    burgerLarge:
      "https://images.pexels.com/photos/1556698/pexels-photo-1556698.jpeg?auto=compress&cs=tinysrgb&w=600",
    // QR code on a real table — hero
    qrTable:
      "https://images.pexels.com/photos/10032377/pexels-photo-10032377.jpeg?auto=compress&cs=tinysrgb&w=400",
    // Who It's For — categories
    restaurantInterior:
      "https://images.pexels.com/photos/12387875/pexels-photo-12387875.jpeg?auto=compress&cs=tinysrgb&w=400",
    takeawayBox:
      "https://images.pexels.com/photos/7266167/pexels-photo-7266167.jpeg?auto=compress&cs=tinysrgb&w=400",
    foodTruck:
      "https://images.pexels.com/photos/11680636/pexels-photo-11680636.jpeg?auto=compress&cs=tinysrgb&w=400",
    cafeCounter:
      "https://images.pexels.com/photos/2467287/pexels-photo-2467287.jpeg?auto=compress&cs=tinysrgb&w=400",
    barInterior:
      "https://images.pexels.com/photos/5490965/pexels-photo-5490965.jpeg?auto=compress&cs=tinysrgb&w=400",
    // Loyalty
    baristaCustomer:
      "https://images.pexels.com/photos/6829507/pexels-photo-6829507.jpeg?auto=compress&cs=tinysrgb&w=300",
    // Why Odijn
    chefCooking:
      "https://images.pexels.com/photos/5779835/pexels-photo-5779835.jpeg?auto=compress&cs=tinysrgb&w=300",
    // Beyond the Menu
    courier:
      "https://images.pexels.com/photos/34366572/pexels-photo-34366572.jpeg",
    ownerTablet:
      "https://images.pexels.com/photos/12935051/pexels-photo-12935051.jpeg",
    // Pricing background
    barInteriorLarge:
      "https://images.pexels.com/photos/5490965/pexels-photo-5490965.jpeg?auto=compress&cs=tinysrgb&w=1200",
  };

  const scrollToSection = (id) => {
    const el = document.querySelector(id);
    if (!el) return;
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 150);
  };

  const [gaStats, setGaStats] = useState(null);

  useEffect(() => {
    let mounted = true;

    const fetchGaStats = async () => {
      try {
        const res = await publicClient.get(
          "/v2/statistic/ga-visitors?site=intro",
        );
        if (mounted) {
          setGaStats(res.data);
        }
      } catch (err) {
        console.error("Footer GA fetch failed:", err);
      }
    };

    fetchGaStats();

    return () => {
      mounted = false;
    };
  }, []);

  const normalizeCount = (value) => {
    if (typeof value !== "number") return 10;
    return value + 10;
  };

  const normalizeCountryName = (country) => {
    if (!country || country.trim() === "") return "Unknown";
    return country;
  };

  const COUNTRY_CODE_MAP = {
    "New Zealand": "NZ",
    Australia: "AU",
    Vietnam: "VN",
    "United States": "US",
    "United Kingdom": "GB",
    Canada: "CA",
    China: "CN",
    Japan: "JP",
    "South Korea": "KR",
    Thailand: "TH",
    Singapore: "SG",
    Malaysia: "MY",
    Indonesia: "ID",
    Philippines: "PH",
    India: "IN",
    France: "FR",
    Germany: "DE",
    Netherlands: "NL",
    Spain: "ES",
    Italy: "IT",
    Brazil: "BR",
    Mexico: "MX",
    "South Africa": "ZA",
  };

  const isoToFlag = (isoCode) => {
    if (!isoCode || isoCode.length !== 2) return "🌐";
    return isoCode
      .toUpperCase()
      .replace(/./g, (char) =>
        String.fromCodePoint(127397 + char.charCodeAt()),
      );
  };

  const countryToFlag = (country) => {
    if (!country || country.trim() === "") return "🌐";
    const iso = COUNTRY_CODE_MAP[country] || country.slice(0, 2).toUpperCase();
    return isoToFlag(iso);
  };

  const countryToCode = (country) => {
    if (!country || country.trim() === "") return "UN";
    return COUNTRY_CODE_MAP[country] || country.slice(0, 2).toUpperCase();
  };

  // --- Loyalty stamp card interaction ---
  const STAMP_TOTAL = 8;
  const [stamped, setStamped] = useState(
    Array.from({ length: STAMP_TOTAL }, (_, i) => i < 5),
  );
  const stampedCount = stamped.filter(Boolean).length;
  const toggleStamp = (i) => {
    setStamped((prev) => prev.map((v, idx) => (idx === i ? !v : v)));
  };

  // --- "Under construction" modal, shown on any live-demo CTA ---
  const [showDemoModal, setShowDemoModal] = useState(false);
  const openDemoModal = () => setShowDemoModal(true);

  const CATEGORIES = [
    {
      icon: <UtensilsCrossed className="w-6 h-6" />,
      title: "Restaurants",
      desc: "Dine-in, table QR ordering, and a kitchen display that stays in sync.",
      accent: "green",
      photo: PHOTOS.restaurantInterior,
    },
    {
      icon: <Package className="w-6 h-6" />,
      title: "Takeaways",
      desc: "Fast pickup ordering with accurate wait times, no phone tag.",
      accent: "red",
      photo: PHOTOS.takeawayBox,
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Food Stalls & Trucks",
      desc: "A menu that travels with you — one QR code, any location.",
      accent: "green",
      photo: PHOTOS.foodTruck,
    },
    {
      icon: <Coffee className="w-6 h-6" />,
      title: "Cafés & Bakeries",
      desc: "Pre-orders for the morning rush, with loyalty built in.",
      accent: "red",
      photo: PHOTOS.cafeCounter,
    },
    {
      icon: <Beer className="w-6 h-6" />,
      title: "Bars & Pubs",
      desc: "Order-to-table for food and drinks, split by round or tab.",
      accent: "green",
      photo: PHOTOS.barInterior,
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "+ Everything Else",
      desc: "Cloud kitchens, food courts, pop-ups — if you serve food and take orders, Odijn fits.",
      accent: "red",
    },
  ];

  return (
    <div className="min-h-screen bg-[#F6F3EC] font-body text-[#14130F] overflow-hidden scroll-smooth">
      <style>{FONT_IMPORT_STYLE}</style>

      {/* NAVBAR */}
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
          navScrolled
            ? "bg-[#14130F] shadow-[0_8px_30px_rgba(0,0,0,0.35)] border-b border-[#F6F3EC]/10"
            : "bg-[#14130F]/80 backdrop-blur-md border-b border-[#F6F3EC]/5"
        }`}
      >
        <div
          className={`max-w-7xl mx-auto flex justify-between items-center px-6 transition-all duration-300 ${
            navScrolled ? "py-3" : "py-4"
          }`}
        >
          <motion.div
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => scrollToSection("#hero")}
          >
            <img
              src="/images/odijn-ngang.png"
              className="h-8 w-auto"
              alt="Odijn"
            />
          </motion.div>

          <div className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const id = item.href.replace("#", "");
              const isActive = activeSection === id;
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className={`relative flex items-center gap-1.5 px-2.5 py-2 text-[13px] font-medium uppercase tracking-wide whitespace-nowrap transition-colors ${
                    isActive
                      ? "text-[#F6F3EC]"
                      : "text-[#A9A79C] hover:text-[#EDEAE0]"
                  }`}
                >
                  <Icon className="w-3.5 h-3.5 shrink-0" />
                  {item.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-2.5 right-2.5 -bottom-[1px] h-[2px] bg-[#B23A2E] rounded-full"
                      transition={{
                        type: "spring",
                        stiffness: 400,
                        damping: 32,
                      }}
                    />
                  )}
                </button>
              );
            })}

            <span className="w-px h-5 bg-[#F6F3EC]/15 mx-2" />

            <motion.button
              onClick={openDemoModal}
              whileHover={{ scale: 1.03 }}
              className="group px-6 py-2.5 rounded-full bg-[#B23A2E] hover:bg-[#8F2C22] text-[#F6F3EC] text-sm font-semibold shadow-md transition-colors whitespace-nowrap flex items-center gap-1.5"
            >
              Start Free Demo
              <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
            </motion.button>
          </div>

          <button
            className="lg:hidden text-[#EDEAE0] p-2 -mr-2 rounded-lg hover:bg-[#F6F3EC]/10 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? (
              <X className="w-6 h-6" />
            ) : (
              <Menu className="w-6 h-6" />
            )}
          </button>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <>
              <motion.div
                key="backdrop"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="fixed inset-0 bg-[#14130F]/90 backdrop-blur-sm z-40 h-screen lg:hidden mt-20"
                onClick={() => setMenuOpen(false)}
              />
              <motion.div
                key="mobile-menu"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.45, ease: [0.25, 0.1, 0.25, 1] }}
                className="fixed top-16 left-0 right-0 z-50 lg:hidden bg-[#14130F] border-t border-[#B23A2E]/25 flex flex-col items-center py-6 space-y-4 shadow-inner"
              >
                {navItems.map((item, index) => {
                  const id = item.href.replace("#", "");
                  const isActive = activeSection === id;
                  const Icon = item.icon;
                  return (
                    <motion.button
                      key={item.label}
                      onClick={() => {
                        setMenuOpen(false);
                        scrollToSection(item.href);
                      }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index, duration: 0.4 }}
                      className={`flex items-center gap-2 text-lg tracking-wide transition-colors ${
                        isActive
                          ? "text-[#F6F3EC] font-semibold"
                          : "text-[#A9A79C] hover:text-[#EDEAE0]"
                      }`}
                    >
                      <Icon className="w-4 h-4 shrink-0" />
                      {item.label}
                    </motion.button>
                  );
                })}

                <span className="w-10 h-px bg-[#F6F3EC]/15 my-1" />

                <motion.button
                  onClick={() => {
                    setMenuOpen(false);
                    openDemoModal();
                  }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  whileHover={{ scale: 1.03 }}
                  className="px-6 py-2.5 rounded-full bg-[#B23A2E] text-[#F6F3EC] text-sm font-semibold shadow-md flex items-center gap-1.5"
                >
                  Start Free Demo
                  <ArrowRight className="w-3.5 h-3.5" />
                </motion.button>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </nav>

      {/* HERO — "The Core of Odijn": the digital menu + online ordering */}
      <section
        id="hero"
        className="relative min-h-screen flex items-center px-6 md:px-16 pt-32 pb-20 bg-[#1E4B3A] overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#F6F3EC 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full grid md:grid-cols-[1.1fr_0.9fr] gap-16 items-center">
          {/* LEFT: COPY */}
          <div>
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-block font-mono-label text-[10px] uppercase text-white bg-reddark border border-[#B23A2E]/40 rounded-full px-3 py-1 mb-4"
            >
              New Zealand, Australia &amp; Canada
            </motion.span>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="font-mono-label text-xs uppercase text-[#E08579] mb-5"
            >
              The Core of Odijn
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-display text-5xl md:text-6xl leading-[1.05] text-[#F6F3EC]"
            >
              One QR code.
              <br />A menu that's always right.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="mt-6 max-w-lg text-[#C9C7BC] text-lg leading-relaxed"
            >
              Build a digital menu once — Odijn keeps prices, photos, and
              sold-out items current everywhere. Customers scan, browse, and
              order directly from their table or the couch. No app to download,
              no printed menu to reprint.
            </motion.p>

            <motion.ul
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="mt-7 space-y-3"
            >
              {[
                "Update pricing & availability in real time",
                "Photos, categories & dietary tags built in",
                "Orders go straight to your kitchen — no middleman",
              ].map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#B23A2E] flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-[#F6F3EC]" />
                  </span>
                  <span className="text-[#EDEAE0]">{point}</span>
                </li>
              ))}
            </motion.ul>

            <motion.div
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.55 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <motion.button
                onClick={openDemoModal}
                whileHover={{ scale: 1.03 }}
                className="px-7 py-3 rounded-full bg-[#B23A2E] hover:bg-[#8F2C22] text-[#F6F3EC] font-semibold shadow-lg transition-colors flex items-center gap-2"
              >
                Free Demo <ArrowRight className="w-4 h-4" />
              </motion.button>
              <button
                onClick={() => scrollToSection("#categories")}
                className="px-7 py-3 rounded-full border border-[#F6F3EC]/30 text-[#F6F3EC] font-semibold hover:bg-[#F6F3EC]/10 transition-colors"
              >
                Who it's for
              </button>
            </motion.div>
          </div>

          {/* RIGHT: SIGNATURE — phone-shaped digital menu mockup + QR chip */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="relative flex justify-center md:justify-end"
          >
            {/* floating QR chip */}
            <div className="absolute -top-6 -left-2 md:left-6 z-20 bg-[#FBF9F4] rounded-xl shadow-xl p-3 flex items-center gap-3 rotate-[-4deg]">
              <div className="grid grid-cols-5 gap-[2px] w-11 h-11 bg-[#14130F] p-1 rounded">
                {[
                  1, 1, 1, 0, 1, 1, 0, 1, 0, 1, 1, 1, 0, 1, 1, 0, 1, 1, 0, 0, 1,
                  0, 1, 1, 1,
                ].map((v, i) => (
                  <span
                    key={i}
                    className={v ? "bg-[#F6F3EC]" : "bg-[#14130F]"}
                  />
                ))}
              </div>
              <span className="font-mono-label text-[10px] uppercase text-[#14130F] leading-tight max-w-[64px]">
                Scan to order
              </span>
            </div>

            {/* real photo — QR code standee on an actual restaurant table */}
            <div className="absolute -bottom-8 -right-3 md:-right-8 z-20 w-32 rotate-[5deg]">
              <img
                src={PHOTOS.qrTable}
                alt="QR code menu on a restaurant table"
                loading="lazy"
                className="w-full aspect-square object-cover rounded-xl shadow-2xl border-4 border-[#FBF9F4]"
              />
              <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-[#B23A2E] text-[#F6F3EC] font-mono-label text-[9px] uppercase px-2 py-1 rounded-full whitespace-nowrap shadow-md">
                On the table
              </span>
            </div>

            {/* phone frame */}
            <div className="w-[280px] bg-[#0F0E0B] rounded-[2.5rem] p-3 shadow-2xl">
              <div className="bg-[#FBF9F4] rounded-[1.8rem] overflow-hidden">
                <div className="h-6 flex items-center justify-center">
                  <div className="w-16 h-1.5 rounded-full bg-[#14130F]/20" />
                </div>

                <div className="px-4 pb-5">
                  <h4 className="font-display text-lg text-[#14130F] mb-3">
                    Odijn Kitchen
                  </h4>

                  <div className="flex gap-2 mb-4 overflow-hidden">
                    <span className="font-mono-label text-[10px] uppercase px-3 py-1.5 rounded-full bg-[#B23A2E] text-[#F6F3EC] shrink-0">
                      All
                    </span>
                    <span className="font-mono-label text-[10px] uppercase px-3 py-1.5 rounded-full border border-[#1E4B3A]/40 text-[#1E4B3A] shrink-0">
                      Mains
                    </span>
                    <span className="font-mono-label text-[10px] uppercase px-3 py-1.5 rounded-full border border-[#1E4B3A]/40 text-[#1E4B3A] shrink-0">
                      Drinks
                    </span>
                  </div>

                  <div className="space-y-3">
                    {[
                      {
                        name: "Beef Pho Bowl",
                        price: "$16.50",
                        photo: PHOTOS.pho,
                      },
                      {
                        name: "Fresh Spring Rolls",
                        price: "$8.00",
                        photo: PHOTOS.springRolls,
                      },
                      {
                        name: "Classic Cheeseburger",
                        price: "$14.00",
                        photo: PHOTOS.burger,
                      },
                      {
                        name: "Crispy Fries",
                        price: "$6.50",
                        photo: PHOTOS.fries,
                      },
                    ].map((item) => (
                      <div
                        key={item.name}
                        className="flex items-center gap-3 pb-3 border-b border-[#14130F]/10 last:border-0"
                      >
                        <img
                          src={item.photo}
                          alt={item.name}
                          loading="lazy"
                          className="w-11 h-11 rounded-lg shrink-0 object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-[#14130F] truncate">
                            {item.name}
                          </p>
                          <p className="font-mono-label text-[11px] text-[#6B6A61]">
                            {item.price}
                          </p>
                        </div>
                        <span className="w-7 h-7 rounded-full bg-[#B23A2E] text-[#F6F3EC] flex items-center justify-center text-sm font-semibold shrink-0">
                          +
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CATEGORIES — who Odijn serves */}
      <section
        id="categories"
        className="px-6 md:px-16 py-24 bg-[#F6F3EC] text-center"
      >
        <p className="font-mono-label text-xs uppercase text-[#B23A2E] mb-3">
          Who It's For
        </p>
        <h2 className="font-display text-4xl md:text-5xl text-[#14130F] mb-4">
          Every kind of food business
        </h2>
        <p className="text-[#4A4940] text-lg max-w-2xl mx-auto mb-14">
          From fine dining to the truck on the corner — if you serve food and
          take orders, Odijn is built to run it.
        </p>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-6 max-w-6xl mx-auto">
          {CATEGORIES.map((cat) => (
            <CategoryCard key={cat.title} {...cat} />
          ))}
        </div>
      </section>

      {/* LOYALTY */}
      <section
        id="loyalty"
        className="relative px-6 md:px-16 py-24 bg-[#1E4B3A] overflow-hidden"
      >
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#F6F3EC 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        />

        <div className="relative z-10 max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          {/* LEFT: COPY */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <p className="font-mono-label text-xs uppercase text-[#E08579] mb-3">
              Loyalty
            </p>
            <h2 className="font-display text-4xl md:text-5xl text-[#F6F3EC] mb-6 leading-tight">
              Every order, remembered.
            </h2>
            <p className="text-[#D9E5DE] text-lg leading-relaxed">
              Odijn turns one-time diners into regulars. Every order stamps
              itself automatically, so local restaurants and cafés get a loyalty
              program that runs without extra effort.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <motion.button
                onClick={openDemoModal}
                whileHover={{ scale: 1.03 }}
                className="px-7 py-3 rounded-full bg-[#B23A2E] hover:bg-[#8F2C22] text-[#F6F3EC] font-semibold shadow-lg transition-colors"
              >
                Start Free Demo
              </motion.button>
              <button
                onClick={() => scrollToSection("#features")}
                className="px-7 py-3 rounded-full border border-[#F6F3EC]/30 text-[#F6F3EC] font-semibold hover:bg-[#F6F3EC]/10 transition-colors"
              >
                See all features
              </button>
            </motion.div>
          </motion.div>

          {/* RIGHT: SIGNATURE — interactive loyalty stamp card + real photo */}
          <motion.div
            initial={{ opacity: 0, y: 30, rotate: -2 }}
            whileInView={{ opacity: 1, y: 0, rotate: -2 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            viewport={{ once: true }}
            className="relative justify-self-center md:justify-self-end"
          >
            {/* real photo, tucked behind the card */}
            <img
              src={PHOTOS.baristaCustomer}
              alt="Barista handing a customer their order"
              loading="lazy"
              className="hidden md:block absolute -left-16 top-6 w-32 h-40 object-cover rounded-xl shadow-xl rotate-[-8deg] border-4 border-[#FBF9F4] -z-10"
            />

            <div className="bg-[#FBF9F4] rounded-2xl shadow-2xl px-7 pt-6 pb-7 w-[300px] border-t-4 border-dashed border-[#B23A2E]">
              <div className="flex items-center justify-between mb-1">
                <span className="font-mono-label text-[10px] uppercase text-[#6B6A61]">
                  Loyalty Card
                </span>
                <Stamp className="w-4 h-4 text-[#B23A2E]" />
              </div>
              <h3 className="font-display text-xl text-[#14130F] mb-4">
                Buy 8, get one free
              </h3>

              <div className="grid grid-cols-4 gap-3 mb-4">
                {stamped.map((isStamped, i) => (
                  <button
                    key={i}
                    onClick={() => toggleStamp(i)}
                    aria-label={`Stamp ${i + 1} ${isStamped ? "filled" : "empty"}`}
                    className={`w-11 h-11 rounded-full flex items-center justify-center transition-all duration-200 ${
                      isStamped
                        ? "bg-[#B23A2E] text-[#F6F3EC] scale-100"
                        : "border-2 border-dashed border-[#1E4B3A]/40 text-transparent hover:border-[#1E4B3A]"
                    }`}
                  >
                    <Check className="w-5 h-5" />
                  </button>
                ))}
              </div>

              <p className="text-sm text-[#6B6A61] leading-snug">
                {stampedCount >= STAMP_TOTAL
                  ? "Free item unlocked — try tapping a stamp."
                  : `${STAMP_TOTAL - stampedCount} more order${
                      STAMP_TOTAL - stampedCount === 1 ? "" : "s"
                    } to a free meal.`}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section id="about" className="px-6 md:px-16 py-24 bg-[#F6F3EC]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          {/* LEFT: TEXT */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <p className="font-mono-label text-xs uppercase text-[#B23A2E] mb-3">
              Why Odijn
            </p>
            <h2 className="font-display text-4xl md:text-5xl text-[#14130F] mb-6">
              One platform. Unlimited ways to serve.
            </h2>
            <p className="text-[#4A4940] text-lg leading-relaxed">
              Whatever you serve and however you serve it, Odijn brings online
              ordering and customer loyalty into one platform — with unlimited
              menu items, unlimited orders, and no hidden caps as you grow.
            </p>
            <p className="text-[#4A4940] text-lg leading-relaxed mt-4">
              No punch cards to lose, no separate app for points. Diners order,
              Odijn stamps the visit, and your regulars keep coming back for the
              reward they're already close to earning.
            </p>

            <motion.button
              onClick={() =>
                window.open("https://business.odijn.com/about", "_blank")
              }
              whileHover={{ scale: 1.03 }}
              className="mt-8 px-8 py-3 rounded-full bg-[#1E4B3A] hover:bg-[#2F6B52] text-[#F6F3EC] font-semibold shadow-lg transition-colors"
            >
              Learn More
            </motion.button>
          </motion.div>

          {/* RIGHT: order-ticket illustration, built from CSS not stock art */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            viewport={{ once: true }}
            className="flex justify-center md:justify-end"
          >
            <div className="relative w-full max-w-sm">
              {/* real photo, peeking out behind the receipt */}
              <img
                src={PHOTOS.chefCooking}
                alt="Chef preparing food in the kitchen"
                loading="lazy"
                className="hidden md:block absolute -bottom-6 -left-10 w-28 h-36 object-cover rounded-xl shadow-xl rotate-[7deg] border-4 border-[#F6F3EC] -z-10"
              />

              <div
                className="bg-[#FBF9F4] rounded-lg shadow-xl px-7 pt-8 pb-10 border border-[#14130F]/10"
                style={{
                  clipPath:
                    "polygon(0% 0%, 100% 0%, 100% 94%, 87.5% 100%, 75% 94%, 62.5% 100%, 50% 94%, 37.5% 100%, 25% 94%, 12.5% 100%, 0% 94%)",
                }}
              >
                <p className="font-mono-label text-[10px] uppercase text-[#6B6A61] mb-1">
                  Order #4471
                </p>
                <h4 className="font-display text-2xl text-[#14130F] mb-5">
                  Odijn Kitchen
                </h4>

                {[
                  ["Beef pho bowl", "$16.50"],
                  ["Fresh spring rolls", "$8.00"],
                  ["Classic cheeseburger", "$14.00"],
                ].map(([item, price]) => (
                  <div
                    key={item}
                    className="flex justify-between text-sm text-[#4A4940] py-2 border-b border-dashed border-[#14130F]/15"
                  >
                    <span>{item}</span>
                    <span className="font-mono-label">{price}</span>
                  </div>
                ))}

                <div className="flex justify-between text-base font-semibold text-[#14130F] pt-3">
                  <span>Total</span>
                  <span className="font-mono-label">$38.50</span>
                </div>
              </div>

              {/* stamp overlay */}
              <div className="absolute -top-4 -right-4 rotate-[-10deg] border-[3px] border-[#B23A2E] text-[#B23A2E] rounded-md px-3 py-1.5 font-mono-label text-xs uppercase bg-[#F6F3EC]/90">
                +1 Stamp
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURES */}
      <section
        id="features"
        className="px-6 md:px-16 py-24 bg-[#14130F] text-center"
      >
        <p className="font-mono-label text-xs uppercase text-[#E08579] mb-3">
          Beyond the Menu
        </p>
        <h2 className="font-display text-4xl md:text-5xl text-[#F6F3EC] mb-14">
          What keeps them coming back
        </h2>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          <FeatureCard
            icon={<Stamp className="w-7 h-7" />}
            title="Loyalty, automatic"
            desc="Every order stamps itself. No app to download, no card to carry — regulars are rewarded without extra steps."
            accent="red"
            photo={PHOTOS.baristaCustomer}
          />
          <FeatureCard
            icon={<ShoppingBag className="w-7 h-7" />}
            title="Pickup & Delivery"
            desc="One order flow covers dine-in, pickup, and delivery, branded to your restaurant end to end."
            accent="green"
            photo={PHOTOS.courier}
          />
          <FeatureCard
            icon={<TrendingUp className="w-7 h-7" />}
            title="Real-Time Insights"
            desc="See which items sell, when regulars return, and how loyalty is turning into repeat revenue."
            accent="green"
            photo={PHOTOS.ownerTablet}
          />
        </div>
      </section>

      {/* MARKETING */}
      <section id="marketing" className="px-6 md:px-16 py-24 bg-[#F6F3EC]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          {/* LEFT: COPY */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <p className="font-mono-label text-xs uppercase text-[#B23A2E] mb-3">
              Marketing
            </p>
            <h2 className="font-display text-4xl md:text-5xl text-[#14130F] mb-6 leading-tight">
              Bring them back, automatically
            </h2>
            <p className="text-[#4A4940] text-lg leading-relaxed">
              Odijn quietly runs the marketing most local kitchens never have
              time for — so regulars keep hearing from you, without anyone
              lifting a finger.
            </p>

            <ul className="mt-7 space-y-3">
              {[
                "Automatic SMS & email when a regular hasn't ordered in a while",
                "One-tap promo codes and percentage-off deals",
                "Shareable menu links for Instagram & TikTok bios",
                "Push notifications for today's specials",
              ].map((point) => (
                <li key={point} className="flex items-start gap-3">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-[#1E4B3A] flex items-center justify-center shrink-0">
                    <Check className="w-3 h-3 text-[#F6F3EC]" />
                  </span>
                  <span className="text-[#4A4940]">{point}</span>
                </li>
              ))}
            </ul>

            <motion.button
              onClick={openDemoModal}
              whileHover={{ scale: 1.03 }}
              className="mt-8 px-8 py-3 rounded-full bg-[#B23A2E] hover:bg-[#8F2C22] text-[#F6F3EC] font-semibold shadow-lg transition-colors"
            >
              Start Free Demo
            </motion.button>
          </motion.div>

          {/* RIGHT: SIGNATURE — a promo push-notification card with a real photo */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            viewport={{ once: true }}
            className="flex justify-center md:justify-end"
          >
            <div className="w-full max-w-sm">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]">
                <img
                  src={PHOTOS.burgerLarge}
                  alt="This week's special"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F0E0B] via-[#0F0E0B]/20 to-transparent" />
                <span className="absolute top-4 left-4 bg-[#B23A2E] text-[#F6F3EC] font-mono-label text-[10px] uppercase px-3 py-1.5 rounded-full">
                  20% Off Today
                </span>
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="font-mono-label text-[10px] uppercase text-[#E08579] mb-1">
                    This Week's Special
                  </p>
                  <h4 className="font-display text-xl text-[#F6F3EC]">
                    Classic Cheeseburger
                  </h4>
                </div>
              </div>

              <div className="mt-4 bg-[#FBF9F4] rounded-xl shadow-lg px-5 py-4 flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#1E4B3A]/12 flex items-center justify-center shrink-0">
                  <Bell className="w-4 h-4 text-[#1E4B3A]" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#14130F]">
                    Promo sent to 340 regulars
                  </p>
                  <p className="font-mono-label text-[11px] text-[#6B6A61]">
                    Just now · via SMS &amp; push
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PRICING */}
      <section
        id="pricing"
        className="relative px-6 md:px-16 py-24 bg-[#1E4B3A] text-center overflow-hidden"
      >
        <img
          src={PHOTOS.barInteriorLarge}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-[0.14] mix-blend-luminosity pointer-events-none"
        />
        <div className="absolute inset-0 bg-[#1E4B3A]/85 pointer-events-none" />
        <div
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(#F6F3EC 1px, transparent 1px)",
            backgroundSize: "20px 20px",
          }}
        />

        <div className="relative z-10">
          <p className="font-mono-label text-xs uppercase text-[#E08579] mb-3">
            Pricing
          </p>
          <h2 className="font-display text-4xl md:text-5xl text-[#F6F3EC] mb-4">
            Start free. Scale when you're ready.
          </h2>
          <p className="text-[#D9E5DE] text-lg max-w-xl mx-auto mb-14">
            No setup fees, no lock-in contracts. Upgrade the day your kitchen
            outgrows the free plan.
          </p>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto text-left">
            {/* STARTER */}
            <div className="bg-[#FBF9F4] rounded-2xl p-8 flex flex-col">
              <p className="font-mono-label text-xs uppercase text-[#6B6A61] mb-2">
                Starter
              </p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-display text-5xl text-[#14130F]">$0</span>
                <span className="text-[#6B6A61]">/month</span>
              </div>
              <p className="text-[#6B6A61] text-sm mb-6">
                Get your first digital menu live, on us.
              </p>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  "1 location",
                  "Digital menu & QR code",
                  "Online ordering",
                  "Basic email support",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#1E4B3A] mt-0.5 shrink-0" />
                    <span className="text-[#4A4940] text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={openDemoModal}
                className="px-6 py-3 rounded-full border-2 border-[#1E4B3A] text-[#1E4B3A] font-semibold hover:bg-[#1E4B3A]/5 transition-colors"
              >
                Start Free
              </button>
            </div>

            {/* GROWTH */}
            <div className="relative bg-[#FBF9F4] rounded-2xl p-8 flex flex-col border-2 border-[#B23A2E] shadow-2xl">
              <span className="absolute -top-3 right-6 bg-[#B23A2E] text-[#F6F3EC] font-mono-label text-[10px] uppercase px-3 py-1 rounded-full flex items-center gap-1">
                <BadgePercent className="w-3 h-3" />
                Most Popular
              </span>
              <p className="font-mono-label text-xs uppercase text-[#B23A2E] mb-2">
                Growth
              </p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-display text-5xl text-[#14130F]">
                  $50
                </span>
                <span className="text-[#6B6A61]">/month</span>
              </div>
              <p className="text-[#6B6A61] text-sm mb-6">
                For kitchens ready to build loyalty and grow.
              </p>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  "Everything in Starter",
                  "Automatic loyalty stamps",
                  "Marketing & promo tools",
                  "Real-time insights",
                  "Multi-location ready",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#B23A2E] mt-0.5 shrink-0" />
                    <span className="text-[#4A4940] text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={openDemoModal}
                className="px-6 py-3 rounded-full bg-[#B23A2E] hover:bg-[#8F2C22] text-[#F6F3EC] font-semibold shadow-lg transition-colors"
              >
                Start Free Demo
              </button>
            </div>

            {/* CUSTOM / AI ADD-ONS */}
            <div className="relative bg-[#14130F] rounded-2xl p-8 flex flex-col border border-[#F6F3EC]/10">
              <span className="absolute -top-3 right-6 bg-[#1E4B3A] text-[#F6F3EC] font-mono-label text-[10px] uppercase px-3 py-1 rounded-full flex items-center gap-1">
                <Sparkle className="w-3 h-3" />
                AI Add-ons
              </span>
              <p className="font-mono-label text-xs uppercase text-[#E08579] mb-2">
                Custom
              </p>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-display text-4xl text-[#F6F3EC]">
                  Contact
                </span>
              </div>
              <p className="text-[#A9A79C] text-sm mb-6">
                Plug in AI to automate more of the restaurant.
              </p>
              <ul className="space-y-3 mb-8 flex-1">
                {[
                  "Everything in Growth",
                  "AI menu & upsell recommendations",
                  "AI chat ordering assistant",
                  "Custom POS & integrations",
                  "Dedicated onboarding",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check className="w-4 h-4 text-[#4C8A6E] mt-0.5 shrink-0" />
                    <span className="text-[#EDEAE0] text-sm">{f}</span>
                  </li>
                ))}
              </ul>
              <a
                href="mailto:sales@odijn.com?subject=Custom%20plan%20enquiry"
                className="px-6 py-3 rounded-full border-2 border-[#F6F3EC]/30 text-[#F6F3EC] font-semibold hover:bg-[#F6F3EC]/10 transition-colors text-center"
              >
                Contact Sales
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-6 md:px-16 py-10 bg-[#0F0E0B] text-[#A9A79C] text-xs">
        <div className="flex flex-col md:flex-row justify-between items-center max-w-6xl mx-auto gap-y-3">
          <div className="flex items-center gap-2">
            <Globe2 className="w-4 h-4 text-[#4C8A6E]" />
            Built in New Zealand — serving Australia &amp; Canada
          </div>

          {gaStats && (
            <div className="flex items-center justify-center gap-3 flex-wrap">
              <div className="flex items-center gap-1">
                <Eye size={14} className="text-[#B23A2E]" />
                <span className="text-[#F6F3EC] font-mono-label">
                  {normalizeCount(gaStats.realtime?.totalUsers)}
                </span>
                <span>online</span>
              </div>

              <span className="text-[#4A4940]">•</span>

              <div className="flex items-center gap-1">
                <Globe size={14} className="text-[#4C8A6E]" />
                <span className="text-[#F6F3EC] font-mono-label">
                  {normalizeCount(gaStats.allTime?.totalUsers)}
                </span>
                <span>visitors</span>
              </div>

              {gaStats.allTime?.byCountry?.length > 0 && (
                <>
                  <span className="text-[#4A4940]">•</span>
                  <div className="font-mono-label text-[11px]">
                    {gaStats.allTime.byCountry
                      .slice(0, 3)
                      .map((c) => {
                        const countryName = normalizeCountryName(c.country);
                        return `${countryToFlag(countryName)} ${normalizeCount(
                          c.users,
                        )}`;
                      })
                      .join(" · ")}
                  </div>
                </>
              )}
            </div>
          )}

          <p>© {new Date().getFullYear()} Odijn. All rights reserved.</p>
        </div>

        {/* PAYMENT METHODS */}
        <div className="max-w-6xl mx-auto mt-6 pt-6 border-t border-[#F6F3EC]/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="font-mono-label text-[10px] uppercase text-[#6B6A61]">
            Secure checkout
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Visa", icon: <CreditCard className="w-3.5 h-3.5" /> },
              {
                name: "Mastercard",
                icon: <CreditCard className="w-3.5 h-3.5" />,
              },
              {
                name: "Apple Pay",
                icon: <Smartphone className="w-3.5 h-3.5" />,
              },
              {
                name: "Google Pay",
                icon: <Smartphone className="w-3.5 h-3.5" />,
              },
              { name: "Stripe", icon: <Zap className="w-3.5 h-3.5" /> },
              { name: "Airwalex", icon: <Building2 className="w-3.5 h-3.5" /> },
            ].map(({ name, icon }) => (
              <span
                key={name}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#F6F3EC]/15 text-[#EDEAE0] text-[11px] font-medium"
              >
                <span className="text-[#4C8A6E]">{icon}</span>
                {name}
              </span>
            ))}
          </div>
        </div>
      </footer>

      {/* DEMO MODAL — shown on every "see demo" style CTA */}
      <AnimatePresence>
        {showDemoModal && (
          <motion.div
            key="demo-modal-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[100] bg-[#0F0E0B]/70 backdrop-blur-sm flex items-center justify-center px-6"
            onClick={() => setShowDemoModal(false)}
          >
            <motion.div
              key="demo-modal"
              initial={{ opacity: 0, y: 20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.96 }}
              transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-[#FBF9F4] rounded-2xl shadow-2xl max-w-sm w-full px-8 py-9 text-center border-t-4 border-[#B23A2E]"
            >
              <button
                onClick={() => setShowDemoModal(false)}
                className="absolute top-4 right-4 text-[#6B6A61] hover:text-[#14130F] transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="w-14 h-14 rounded-full bg-[#1E4B3A]/10 flex items-center justify-center mx-auto mb-5">
                <Hammer className="w-6 h-6 text-[#1E4B3A]" />
              </div>

              <h3 className="font-display text-2xl text-[#14130F] mb-3">
                Still under construction
              </h3>
              <p className="text-[#4A4940] leading-relaxed">
                This software is currently being built. Odijn will officially
                launch in 2027 — thanks for your patience.
              </p>

              <button
                onClick={() => setShowDemoModal(false)}
                className="mt-7 px-7 py-2.5 rounded-full bg-[#B23A2E] hover:bg-[#8F2C22] text-[#F6F3EC] font-semibold transition-colors"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function FeatureCard({ icon, title, desc, accent, photo }) {
  const accentColor = accent === "red" ? "#B23A2E" : "#1E4B3A";
  const accentBg = accent === "red" ? "bg-[#B23A2E]/15" : "bg-[#1E4B3A]/25";

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className="bg-[#FBF9F4] rounded-2xl overflow-hidden text-left border border-[#F6F3EC]/10 hover:border-[#B23A2E]/40 shadow-lg transition-all"
    >
      {photo && (
        <div className="relative h-40 w-full">
          <img
            src={photo}
            alt={title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#14130F]/55 to-transparent" />
        </div>
      )}
      <div className={photo ? "p-8 -mt-9 relative" : "p-8"}>
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center mb-5 shadow-md ${
            photo ? "bg-[#FBF9F4]" : accentBg
          }`}
          style={{ color: accentColor }}
        >
          {icon}
        </div>
        <h3 className="font-display text-xl text-[#14130F] mb-2">{title}</h3>
        <p className="text-[#6B6A61] leading-relaxed">{desc}</p>
      </div>
    </motion.div>
  );
}

function CategoryCard({ icon, title, desc, accent, photo }) {
  const accentColor = accent === "red" ? "#B23A2E" : "#1E4B3A";
  const accentBg = accent === "red" ? "bg-[#B23A2E]/12" : "bg-[#1E4B3A]/12";
  const isWildcard = title === "+ Everything Else";

  return (
    <motion.div
      whileHover={{ y: -4 }}
      className={`rounded-2xl overflow-hidden text-left border transition-all ${
        isWildcard
          ? "bg-[#14130F] border-[#B23A2E]/40"
          : "bg-[#FBF9F4] border-[#14130F]/10 hover:border-[#B23A2E]/40 shadow-sm hover:shadow-md"
      }`}
    >
      {photo && (
        <div className="relative h-40 w-full">
          <img
            src={photo}
            alt={title}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#14130F]/50 to-transparent" />
        </div>
      )}
      <div className={photo ? "p-3 -mt-8 relative" : "p-3"}>
        <div
          className={`w-11 h-11 rounded-full flex items-center justify-center mb-4 shadow-md ${
            isWildcard ? "bg-[#B23A2E]/20" : photo ? "bg-[#FBF9F4]" : accentBg
          }`}
          style={{ color: isWildcard ? "#E08579" : accentColor }}
        >
          {icon}
        </div>
        <h3
          className={`font-display text-lg mb-1.5 ${
            isWildcard ? "text-[#F6F3EC]" : "text-[#14130F]"
          }`}
        >
          {title}
        </h3>
        <p
          className={`text-sm leading-relaxed ${
            isWildcard ? "text-[#C9C7BC]" : "text-[#6B6A61]"
          }`}
        >
          {desc}
        </p>
      </div>
    </motion.div>
  );
}
// import { useEffect, useState } from "react";
// import { motion } from "framer-motion";
// import { AnimatePresence } from "framer-motion";

// import {
//   ArrowRight,
//   Globe2,
//   CalendarDays,
//   Gift,
//   ShoppingBag,
//   Menu,
//   X,
//   Globe,
//   Eye,
// } from "lucide-react";
// import { publicClient } from "../config/axiosConfig";

// export default function IntroOdijn() {
//   const [menuOpen, setMenuOpen] = useState(false);

//   const navItems = [
//     { label: "Home", href: "#hero" },
//     { label: "About", href: "#about" },
//     { label: "Features", href: "#features" },
//   ];

//   // 👇 Smooth scroll handler with gentle delay
//   const scrollToSection = (id) => {
//     const el = document.querySelector(id);
//     if (!el) return;
//     setTimeout(() => {
//       el.scrollIntoView({ behavior: "smooth", block: "start" });
//     }, 150); // delay 150ms before scroll starts
//   };

//   const [gaStats, setGaStats] = useState(null);

//   useEffect(() => {
//     let mounted = true;

//     const fetchGaStats = async () => {
//       try {
//         const res = await publicClient.get(
//           "/v2/statistic/ga-visitors?site=intro"
//         );
//         if (mounted) {
//           setGaStats(res.data);
//         }
//       } catch (err) {
//         console.error("Footer GA fetch failed:", err);
//       }
//     };

//     fetchGaStats();

//     return () => {
//       mounted = false;
//     };
//   }, []);

//   const normalizeCount = (value) => {
//     if (typeof value !== "number") return 10;
//     return value + 10;
//   };

//   const normalizeCountryName = (country) => {
//     if (!country || country.trim() === "") return "Unknown";
//     return country;
//   };

//   const COUNTRY_CODE_MAP = {
//     "New Zealand": "NZ",
//     Australia: "AU",
//     Vietnam: "VN",

//     "United States": "US",
//     "United Kingdom": "GB",
//     Canada: "CA",

//     China: "CN",
//     Japan: "JP",
//     "South Korea": "KR",
//     Thailand: "TH",
//     Singapore: "SG",
//     Malaysia: "MY",
//     Indonesia: "ID",
//     Philippines: "PH",

//     India: "IN",

//     France: "FR",
//     Germany: "DE",
//     Netherlands: "NL",
//     Spain: "ES",
//     Italy: "IT",

//     Brazil: "BR",
//     Mexico: "MX",
//     "South Africa": "ZA",
//   };

//   const isoToFlag = (isoCode) => {
//     if (!isoCode || isoCode.length !== 2) return "🌐";

//     return isoCode
//       .toUpperCase()
//       .replace(/./g, (char) =>
//         String.fromCodePoint(127397 + char.charCodeAt())
//       );
//   };

//   const countryToFlag = (country) => {
//     if (!country || country.trim() === "") return "🌐";

//     const iso = COUNTRY_CODE_MAP[country] || country.slice(0, 2).toUpperCase();

//     return isoToFlag(iso);
//   };

//   const countryToCode = (country) => {
//     if (!country || country.trim() === "") return "UN";

//     return COUNTRY_CODE_MAP[country] || country.slice(0, 2).toUpperCase();
//   };

//   return (
//     <div className="min-h-screen bg-gradient-to-b from-gray-950 to-gray-900 text-white font-sans overflow-hidden scroll-smooth">
//       {/* NAVBAR */}
//       <nav className="fixed top-0 left-0 w-full z-50 bg-gray-950/80 backdrop-blur-md border-b border-gray-800">
//         <div className="max-w-7xl mx-auto flex justify-between items-center px-6 py-4">
//           {/* Logo */}
//           <div
//             className="flex items-center gap-2 cursor-pointer"
//             onClick={() => scrollToSection("#hero")}
//           >
//             <img src="/images/odijn-ngang.png" className="h-8 w-auto" />
//           </div>

//           {/* Desktop Menu */}
//           <div className="hidden md:flex gap-8 items-center">
//             {navItems.map((item) => (
//               <button
//                 key={item.label}
//                 onClick={() => scrollToSection(item.href)}
//                 className="text-gray-300 hover:text-rose-400 transition"
//               >
//                 {item.label}
//               </button>
//             ))}
//             <motion.button
//               onClick={() => window.open("https://demo.odijn.com", "_blank")}
//               initial={{ opacity: 0, y: 10 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.3, duration: 0.5 }}
//               whileHover={{ scale: 1.05 }}
//               className="px-6 py-2 rounded-full bg-gradient-to-r from-rose-500 to-red-600 text-sm font-semibold shadow hover:shadow-rose-700/40 transition"
//             >
//               Launching Demo
//             </motion.button>
//           </div>

//           {/* Mobile Menu Toggle */}
//           <button
//             className="md:hidden text-gray-300"
//             onClick={() => setMenuOpen(!menuOpen)}
//           >
//             {menuOpen ? (
//               <X className="w-6 h-6" />
//             ) : (
//               <Menu className="w-6 h-6" />
//             )}
//           </button>
//         </div>

//         {/* Mobile Menu */}
//         <AnimatePresence>
//           {menuOpen && (
//             <>
//               {/* BACKDROP OVERLAY */}
//               <motion.div
//                 key="backdrop"
//                 initial={{ opacity: 0 }}
//                 animate={{ opacity: 0.8 }}
//                 exit={{ opacity: 0 }}
//                 transition={{ duration: 0.4 }}
//                 className="fixed inset-0 bg-black z-40 h-screen md:hidden mt-20"
//                 onClick={() => setMenuOpen(false)} // tap outside closes menu
//               />

//               {/* MOBILE MENU PANEL */}
//               <motion.div
//                 key="mobile-menu"
//                 initial={{ opacity: 0, y: -20 }}
//                 animate={{ opacity: 1, y: 0 }}
//                 exit={{ opacity: 0, y: -15 }}
//                 transition={{
//                   duration: 0.45,
//                   ease: [0.25, 0.1, 0.25, 1],
//                 }}
//                 className="fixed top-16 left-0 right-0 z-50 md:hidden bg-gray-900 border-t border-gray-800 flex flex-col items-center py-6 space-y-4 shadow-inner rounded-b-2xl"
//               >
//                 {navItems.map((item, index) => (
//                   <motion.button
//                     key={item.label}
//                     onClick={() => {
//                       setMenuOpen(false);
//                       scrollToSection(item.href);
//                     }}
//                     initial={{ opacity: 0, y: 10 }}
//                     animate={{ opacity: 1, y: 0 }}
//                     transition={{ delay: 0.1 * index, duration: 0.4 }}
//                     className="text-gray-300 hover:text-rose-400 text-lg tracking-wide"
//                   >
//                     {item.label}
//                   </motion.button>
//                 ))}

//                 <motion.button
//                   onClick={() =>
//                     window.open("https://demo.odijn.com", "_blank")
//                   }
//                   initial={{ opacity: 0, y: 10 }}
//                   animate={{ opacity: 1, y: 0 }}
//                   transition={{ delay: 0.3, duration: 0.5 }}
//                   whileHover={{ scale: 1.05 }}
//                   className="px-6 py-2 rounded-full bg-gradient-to-r from-rose-500 to-red-600 text-sm font-semibold shadow hover:shadow-rose-700/40 transition"
//                 >
//                   Launching Demo
//                 </motion.button>
//               </motion.div>
//             </>
//           )}
//         </AnimatePresence>
//       </nav>
//       {/* HERO */}
//       <section
//         id="hero"
//         className="relative min-h-screen flex flex-col items-center justify-center text-center px-6 py-36 md:py-44 overflow-hidden"
//         style={{
//           backgroundImage:
//             "linear-gradient(to bottom, rgba(0,0,0,0.7), rgba(0,0,0,0.5)), url('/images/odijn26.png')",
//           backgroundSize: "cover",
//           backgroundPosition: "center",
//           backgroundAttachment: "fixed",
//           animation: "slidein 20s infinite alternate ease-in-out",
//         }}
//       >
//         {/* DARK OVERLAY (optional, extra depth) */}
//         <div className="absolute inset-0 bg-black/40"></div>

//         {/* CONTENT */}
//         <div className="relative z-10 flex flex-col items-center justify-center text-center">
//           {/* LOGO ICON */}
//           <motion.img
//             initial={{ opacity: 0, scale: 0.8 }}
//             animate={{ opacity: 1, scale: 1 }}
//             transition={{ duration: 0.8 }}
//             src="/images/logo.png"
//             alt="Odijn Icon"
//             className="w-32 md:w-52 mb-2 select-none"
//           />

//           {/* MAIN HEADING */}

//           {/* SUBHEADING */}
//           <motion.p
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ delay: 0.3 }}
//             className="mt-2 text-xl md:text-2xl font-bold text-gray-300 tracking-wide"
//           >
//             Born to Connect
//           </motion.p>

//           {/* DESCRIPTION */}
//           <motion.p
//             initial={{ opacity: 0, y: 20 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ delay: 0.5 }}
//             className="mt-6 max-w-2xl mx-auto text-gray-300 text-lg"
//           >
//             The next-generation SaaS platform empowering local businesses to
//             grow — through booking, ordering, and loyalty.
//           </motion.p>

//           {/* CTA BUTTON */}
//           <motion.button
//             onClick={() => (window.location.href = "https://demo.odijn.com")}
//             initial={{ opacity: 0, y: 10 }}
//             animate={{ opacity: 1, y: 0 }}
//             transition={{ delay: 0.3, duration: 0.5 }}
//             whileHover={{ scale: 1.05 }}
//             className="px-6 py-2 mt-4 rounded-full bg-gradient-to-r from-rose-500 to-red-600 text-sm font-semibold shadow hover:shadow-rose-700/40 transition"
//           >
//             Launching Demo <ArrowRight className="w-5 h-5 inline-block ml-1" />
//           </motion.button>
//         </div>
//       </section>

//       {/* ABOUT */}
//       <section
//         id="about"
//         className="min-h-screen px-6 md:px-20 py-24 bg-gray-900/40 backdrop-blur-sm border-t border-gray-800 flex flex-col justify-center"
//       >
//         <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
//           {/* LEFT: TEXT */}
//           <motion.div
//             initial={{ opacity: 0, x: -40 }}
//             whileInView={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.7 }}
//             viewport={{ once: true }}
//             className="text-center md:text-left"
//           >
//             <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-rose-400 to-red-600">
//               Why Odijn?
//             </h2>
//             <p className="text-gray-400 text-lg leading-relaxed">
//               From nail salons, beauty and massage studios to restaurants,
//               cafés, and takeaways —{" "}
//               <span className="text-rose-400 font-medium">Odijn</span> brings
//               everything together in one simple platform.
//             </p>
//             <p className="text-gray-400 text-lg leading-relaxed mt-4">
//               Seamlessly connect with your customers, automate operations, and
//               grow loyalty effortlessly — empowering local businesses to thrive
//               in the digital era.
//             </p>

//             <motion.button
//               onClick={() =>
//                 window.open("https://business.odijn.com/about", "_blank")
//               }
//               whileHover={{ scale: 1.05 }}
//               className="mt-8 px-8 py-3 rounded-full bg-gradient-to-r from-rose-500 to-red-600 text-white font-semibold shadow-lg hover:shadow-rose-700/40"
//             >
//               Learn More
//             </motion.button>
//           </motion.div>

//           {/* RIGHT: IMAGE / ILLUSTRATION */}
//           <motion.div
//             initial={{ opacity: 0, x: 40 }}
//             whileInView={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.7, delay: 0.2 }}
//             viewport={{ once: true }}
//             className="flex justify-center md:justify-end"
//           >
//             <img
//               src="/images/weebill_square.jpg"
//               alt="Odijn platform preview"
//               className="w-full max-w-md rounded-2xl shadow-lg border border-gray-800 hover:border-rose-500/40 transition-all duration-300"
//             />
//           </motion.div>
//         </div>
//       </section>
//       {/* FEATURES */}
//       <section
//         id="features"
//         className="px-6 md:px-20 py-20 bg-gray-950 text-center"
//       >
//         <h2 className="text-3xl md:text-4xl font-bold mb-12">
//           Powerful All-in-One Tools
//         </h2>
//         <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto">
//           <FeatureCard
//             icon={<CalendarDays className="w-10 h-10 text-rose-500" />}
//             title="Smart Booking"
//             desc="Easy online booking with dynamic staff scheduling and instant confirmations."
//           />
//           <FeatureCard
//             icon={<ShoppingBag className="w-10 h-10 text-rose-500" />}
//             title="Online Ordering"
//             desc="Seamless pickup, delivery, and table ordering system for your store."
//           />
//           <FeatureCard
//             icon={<Gift className="w-10 h-10 text-rose-500" />}
//             title="Loyalty System"
//             desc="Reward repeat customers with digital stamps, points, and personalized offers."
//           />
//         </div>
//       </section>
//       {/* FOOTER */}
//       <footer className="px-6 md:px-20 py-12 border-t border-gray-800 text-center text-gray-400 text-sm">
//         <div className=" flex flex-col md:flex-row justify-between items-center max-w-6xl mx-auto gap-y-2 text-xs">
//           <div className="flex justify-center items-center gap-2">
//             <Globe2 className="w-4 h-4" />
//             Built in New Zealand — to Australia & Canada
//           </div>
//           {gaStats && (
//             <div className="mb-0 flex flex-col items-center gap-1">
//               {/* MAIN LINE */}
//               <div className="flex items-center justify-center gap-3">
//                 <div className="flex items-center gap-1">
//                   <Eye size={14} className="" />
//                   <span className="text-neonYellow animate-neonFlicker">
//                     {normalizeCount(gaStats.realtime?.totalUsers)}
//                   </span>
//                   <span className="">online</span>
//                 </div>

//                 <span className="">•</span>

//                 <div className="flex items-center gap-1">
//                   <Globe size={14} className="" />
//                   <span className="text-neonYellow animate-neonFlicker">
//                     {normalizeCount(gaStats.allTime?.totalUsers)}
//                   </span>
//                   <span className="">visitors</span>
//                 </div>

//                 {gaStats.allTime?.byCountry?.length > 0 && (
//                   <>
//                     <span className="">•</span>

//                     <div className="text-[11px] ">
//                       {" "}
//                       {gaStats?.allTime?.byCountry
//                         .slice(0, 3)
//                         .map((c) => {
//                           const countryName = normalizeCountryName(c.country);
//                           const code = countryToCode(countryName);

//                           return `${countryToFlag(c.country)} ${normalizeCount(
//                             c.users
//                           )}`;
//                         })
//                         .join(" · ")}
//                     </div>
//                   </>
//                 )}
//               </div>

//               {/* BREAKDOWN */}
//             </div>
//           )}
//           <p className="mt-0">
//             © {new Date().getFullYear()} Odijn. All rights reserved.
//           </p>
//         </div>
//       </footer>
//     </div>
//   );
// }

// function FeatureCard({ icon, title, desc }) {
//   return (
//     <motion.div
//       whileHover={{ y: -5 }}
//       className="bg-gray-900/70 rounded-2xl p-8 border border-gray-800 hover:border-rose-500/50 transition-all"
//     >
//       <div className="flex justify-center mb-4">{icon}</div>
//       <h3 className="text-xl font-semibold mb-3">{title}</h3>
//       <p className="text-gray-400">{desc}</p>
//     </motion.div>
//   );
// }
